import React, { useState } from 'react';
import { ITicketProps } from '../../types/Ticket/ITicket';
import { GameplanApi } from '../../redux/api/GameplanApi';
import { IAllocation, IAllocationData } from '../../types/Gameplan/IAllocation';
import { toDigital, toMinutes } from '../../utils/utils';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { ITeam } from '../../types/ITeam';
import { UserApi } from '../../redux/api/UserApi';
import './EditAllocation.scss'; // Assuming you have a CSS file for styles
import Time from '../Common/Time';
import { PartialSpinny } from '../Nav/Spinny';

export interface ITicketAllocation extends ITicketProps {
    allocationData: IAllocationData;
    onAllocationChange: (e: IAllocationData) => void;
}

const EditAllocation: React.FC<ITicketAllocation> = (ticket) => {
    const { data: allocation } = GameplanApi.useGetTicketAllocationQuery({ ticketId: ticket.ID }, { skip: !ticket.ID || !ticket.Is_Workstream });
    const [filter, setFilter] = useState('');
    //const [editedAllocation, setEditedAllocation] = useState<IAllocationData>([]);
    const { data: userProfile } = GameplanApi.useGetUserProfileQuery();
    const { data: teams } = UserApi.useGetTeamsQuery();
    const [team, setTeam] = useState<ITeam>(null);
    //const [effort, setEffort] = useState<string>('0:00');
    const { data: staff, isLoading } = UserApi.useGetStaffQuery({ teamId: null, includeQueues: false, includeStaffPlusForCompanyId: ticket?.Company_Id }, { skip: !ticket?.Company_Id });
    const { data: workstream } = GameplanApi.useGetTicketWorkstreamQuery({ ticketId: ticket.ID }, { skip: !ticket.ID || !ticket.Is_Workstream });
    //Load the effort into state
    // React.useEffect(() => {
    //     if (workstream) {
    //         //setEffort(toDigital(workstream.Effort_Mins));
    //         ticket.onAllocationChange && ticket.onAllocationChange({ ...ticket.allocationData, Effort_Mins: toDigital(workstream.Effort_Mins) });
    //     }
    // }, [workstream]);

    //Load the allocation into the state when the component mounts
    React.useEffect(() => {
        if (allocation && staff && workstream) {
            //Merge staff and allocation data
            let allocationData = allocation.filter(a => a.Contact_Id > 0).map(user => ({ ...user, Checked: true, Team_Name: staff.find(s => s.ID === user.Contact_Id)?.Team_Name } as IAllocation));
            allocationData = allocationData.concat(staff.filter(s => !allocation.some(a => a.Contact_Id === s.ID)).map(user => ({ Contact_Id: user.ID, Full_Name: user.Full_Name, Assigned_Percent: 0, Assigned_Mins: 0, Checked: false, Team_Name: user.Team_Name } as IAllocation)));

            //No total row
            ticket.onAllocationChange && ticket.onAllocationChange({ ...ticket.allocationData, Allocation: allocationData, Effort_Mins: toDigital(workstream.Effort_Mins) });
        }
    }, [allocation, staff, workstream]);

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);
    };

    const handleCheckboxChange = (userId: number) => {
        ticket.onAllocationChange && ticket.onAllocationChange({
            ...ticket.allocationData, Allocation: ticket.allocationData?.Allocation?.map((user) =>
                user.Contact_Id === userId ? { ...user, Checked: !user.Checked } : user
            )
        });
    };

    const handlePercentageChange = (userId: number, percentage: number) => {
        let newTIme = toMinutes(ticket?.allocationData?.Effort_Mins) * (percentage / 100);
        ticket.onAllocationChange && ticket.onAllocationChange({
            ...ticket.allocationData, Allocation: ticket.allocationData?.Allocation?.map((user) =>
                user.Contact_Id === userId ? { ...user, Assigned_Percent: percentage, Assigned_Mins: newTIme } : user
            )
        });
    };

    const filteredUsers = ticket.allocationData?.Allocation?.filter((user) =>
        user.Full_Name.toLowerCase().includes(filter.toLowerCase()) && (!team || user.Team_Name === team.Dept_Name)
    ).map((user) => {
        return { ...user, Assigned_Mins: toMinutes(ticket?.allocationData?.Effort_Mins || '') * (user.Assigned_Percent / 100) }
    }) || [];

    const totalPercent = ticket.allocationData?.Allocation?.reduce((total, user) => total + (user.Checked ? user.Assigned_Percent : 0), 0);

    return (
        <div className="container edit-allocation">
            {isLoading && <PartialSpinny />}
            <div className="row">
                <div className="col">
                </div>
                <div className='col-2 justify-content-end align-items-center flex'><strong>Total Effort:</strong></div>
                <div className='col-2 flex m-1 effort-min'> <input className="form-control" required placeholder='hh:mm' value={ticket?.allocationData?.Effort_Mins}
                    onChange={(e) => {
                        ticket.onAllocationChange && ticket.onAllocationChange({
                            ...ticket.allocationData, Effort_Mins: e.target.value
                        });
                    }}
                    onBlur={(e) => {
                        //Recalculate all the Assigned_Mins based on the new Effort_Mins
                        ticket.onAllocationChange && ticket.onAllocationChange({
                            Allocation: ticket.allocationData.Allocation.map((a) => {
                                return { ...a, Assigned_Mins: toMinutes(e.target.value) * (a.Assigned_Percent / 100) }
                            }), Effort_Mins: toDigital(toMinutes(e.target.value))
                        });

                    }} /></div>
            </div>
            <div className="row mt-2 mb-1">
                <div className="col-12">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Filter by name"
                            value={filter}
                            onChange={handleFilterChange}
                        />
                        <span className="input-group-text">
                            <ComboBox style={{ width: 200 }} className="form-control123" filterable={false} allowCustom={false} placeholder={`Filter by ${userProfile?.Phrases.Team}`} data={teams} value={team} textField='Dept_Name' onChange={(e) => setTeam(e.value)} />
                        </span>
                    </div>
                </div>
                <div className="col-6">
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="table-responsive">
                        <table className="table table-bordered">
                            <thead className="thead-light">
                                <tr>
                                    <th className='col-1 text-center'>Assign</th>
                                    <th>User</th>
                                    <th className='col-2'>Allocation (%)</th>
                                    <th className='col-2'>Hours</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <tr key={user.Contact_Id}>
                                        <td className='text-center'>
                                            <input
                                                type="checkbox"
                                                checked={user.Checked}
                                                onChange={() => handleCheckboxChange(user.Contact_Id)}
                                            />
                                        </td>
                                        <td>{user.Full_Name}</td>
                                        <td>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={user.Assigned_Percent}
                                                onChange={(e) =>
                                                    handlePercentageChange(user.Contact_Id, (Number(e.target.value) > 0 && Number(e.target.value) <= 100) ? Number(e.target.value) : 0)
                                                }
                                                disabled={!user.Checked}
                                            />
                                        </td>
                                        <td>{toDigital(user.Assigned_Mins)}</td>
                                    </tr>
                                ))}
                            </tbody>

                            <tfoot>
                                <tr>
                                    <td colSpan={2} className="text-end"><strong>Total Allocation (%)</strong></td>
                                    <td className={totalPercent != 100 ? 'warning' : 'good'}>
                                        {totalPercent}
                                    </td>
                                    <td>
                                        <div className="text-left">
                                            {toDigital(ticket.allocationData?.Allocation?.reduce((total, user) => total + (user.Checked ? user.Assigned_Mins : 0), 0))}
                                        </div>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
            {totalPercent != 100 && (
                <div className="alert alert-danger mt-2" role="alert">
                    Total allocation does not equal 100%. Please adjust the allocation percentages.
                </div>)}
        </div>
    );

};

export default EditAllocation;